import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Tooltip,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Loader,
  Center,
  Tabs,
  ScrollArea,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconCheck,
  IconX,
  IconRefresh,
} from "@tabler/icons-react";
import {
  useSpecimenMacrosAdmin,
  useCreateSpecimenMacro,
  useUpdateSpecimenMacro,
  useToggleSpecimenMacroActive,
  useSpecimenBatchTemplatesAdmin,
  useCreateSpecimenBatchTemplate,
  useUpdateSpecimenBatchTemplate,
  useToggleSpecimenBatchTemplateActive,
} from "../../../hooks/admin/useSpecimenMacros";
import SpecimenMacroForm, {
  SpecimenMacroFormValues,
} from "../../../components/admin/specimen-macros/SpecimenMacroForm";
import BatchTemplateForm, {
  BatchTemplateFormValues,
} from "../../../components/admin/specimen-macros/BatchTemplateForm";
import {
  SpecimenMacro,
  SpecimenBatchTemplate,
} from "../../../services/admin/specimenMacro.service";

const ITEMS_PER_PAGE = 20;

const MATERIAL_LABELS: Record<string, string> = {
  formalin_jar: "Formalin jar",
  block: "Block",
  slides_only: "Slides only",
  blood_tube: "Blood tube",
  urine_sample: "Urine sample",
  stool_collection: "Stool collection",
};

export default function SpecimenMacrosAdminPage() {
  const [activeTab, setActiveTab] = useState<string | null>("macros");

  // Macro state
  const [macroFormOpened, { open: openMacroForm, close: closeMacroForm }] =
    useDisclosure(false);
  const [
    macroConfirmOpened,
    { open: openMacroConfirm, close: closeMacroConfirm },
  ] = useDisclosure(false);
  const [editingMacro, setEditingMacro] = useState<SpecimenMacro | null>(null);
  const [macroToToggle, setMacroToToggle] = useState<SpecimenMacro | null>(
    null
  );
  const [macroSearch, setMacroSearch] = useState("");
  const [debouncedMacroSearch] = useDebouncedValue(macroSearch, 300);
  const [macroPage, setMacroPage] = useState(1);

  // Batch state
  const [batchFormOpened, { open: openBatchForm, close: closeBatchForm }] =
    useDisclosure(false);
  const [
    batchConfirmOpened,
    { open: openBatchConfirm, close: closeBatchConfirm },
  ] = useDisclosure(false);
  const [editingBatch, setEditingBatch] =
    useState<SpecimenBatchTemplate | null>(null);
  const [batchToToggle, setBatchToToggle] =
    useState<SpecimenBatchTemplate | null>(null);
  const [batchSearch, setBatchSearch] = useState("");
  const [debouncedBatchSearch] = useDebouncedValue(batchSearch, 300);
  const [batchPage, setBatchPage] = useState(1);

  // Macro hooks
  const {
    data: macros = [],
    isLoading: macrosLoading,
    refetch: refetchMacros,
  } = useSpecimenMacrosAdmin();
  const createMacro = useCreateSpecimenMacro();
  const updateMacro = useUpdateSpecimenMacro();
  const toggleMacroActive = useToggleSpecimenMacroActive();

  // Batch hooks
  const {
    data: batchTemplates = [],
    isLoading: batchLoading,
    refetch: refetchBatch,
  } = useSpecimenBatchTemplatesAdmin();
  const createBatch = useCreateSpecimenBatchTemplate();
  const updateBatch = useUpdateSpecimenBatchTemplate();
  const toggleBatchActive = useToggleSpecimenBatchTemplateActive();

  // Macro filtering/pagination
  const filteredMacros = useMemo(() => {
    if (!debouncedMacroSearch) return macros;
    const q = debouncedMacroSearch.toLowerCase();
    return macros.filter(
      (m) =>
        m.code.toLowerCase().includes(q) ||
        (m.description && m.description.toLowerCase().includes(q)) ||
        (m.source?.name && m.source.name.toLowerCase().includes(q)) ||
        (m.subsite?.name && m.subsite.name.toLowerCase().includes(q))
    );
  }, [macros, debouncedMacroSearch]);

  const macroTotalPages = Math.ceil(filteredMacros.length / ITEMS_PER_PAGE);
  const paginatedMacros = useMemo(() => {
    const start = (macroPage - 1) * ITEMS_PER_PAGE;
    return filteredMacros.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredMacros, macroPage]);

  // Batch filtering/pagination
  const filteredBatches = useMemo(() => {
    if (!debouncedBatchSearch) return batchTemplates;
    const q = debouncedBatchSearch.toLowerCase();
    return batchTemplates.filter((t) => t.name.toLowerCase().includes(q));
  }, [batchTemplates, debouncedBatchSearch]);

  const batchTotalPages = Math.ceil(filteredBatches.length / ITEMS_PER_PAGE);
  const paginatedBatches = useMemo(() => {
    const start = (batchPage - 1) * ITEMS_PER_PAGE;
    return filteredBatches.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredBatches, batchPage]);

  // Macro handlers
  const handleAddMacro = () => {
    setEditingMacro(null);
    openMacroForm();
  };

  const handleEditMacro = (macro: SpecimenMacro) => {
    setEditingMacro(macro);
    openMacroForm();
  };

  const handleMacroSubmit = async (values: SpecimenMacroFormValues) => {
    if (editingMacro) {
      await updateMacro.mutateAsync({ id: editingMacro.id, ...values });
    } else {
      await createMacro.mutateAsync(values);
    }
    closeMacroForm();
  };

  const handleToggleMacro = (macro: SpecimenMacro) => {
    setMacroToToggle(macro);
    openMacroConfirm();
  };

  const confirmToggleMacro = async () => {
    if (macroToToggle) {
      await toggleMacroActive.mutateAsync(macroToToggle.id);
      closeMacroConfirm();
      setMacroToToggle(null);
    }
  };

  // Batch handlers
  const handleAddBatch = () => {
    setEditingBatch(null);
    openBatchForm();
  };

  const handleEditBatch = (template: SpecimenBatchTemplate) => {
    setEditingBatch(template);
    openBatchForm();
  };

  const handleBatchSubmit = async (values: BatchTemplateFormValues) => {
    if (editingBatch) {
      await updateBatch.mutateAsync({ id: editingBatch.id, ...values });
    } else {
      await createBatch.mutateAsync(values);
    }
    closeBatchForm();
  };

  const handleToggleBatch = (template: SpecimenBatchTemplate) => {
    setBatchToToggle(template);
    openBatchConfirm();
  };

  const confirmToggleBatch = async () => {
    if (batchToToggle) {
      await toggleBatchActive.mutateAsync(batchToToggle.id);
      closeBatchConfirm();
      setBatchToToggle(null);
    }
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Specimen Macros</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage macro codes for rapid specimen data entry during
                accessioning
              </Text>
            </div>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={activeTab === "macros" ? handleAddMacro : handleAddBatch}
            >
              {activeTab === "macros" ? "Add Macro" : "Add Batch Template"}
            </Button>
          </Group>

          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="macros">
                Per-Specimen Macros ({macros.length})
              </Tabs.Tab>
              <Tabs.Tab value="batch">
                Batch Templates ({batchTemplates.length})
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="macros" pt="md">
              <Stack gap="md">
                <Paper p="md" withBorder>
                  <Group>
                    <TextInput
                      placeholder="Search by code, description, source, or subsite..."
                      value={macroSearch}
                      onChange={(e) => {
                        setMacroSearch(e.target.value);
                        setMacroPage(1);
                      }}
                      leftSection={<IconSearch size={16} />}
                      style={{ flex: 1 }}
                    />
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      onClick={() => refetchMacros()}
                      loading={macrosLoading}
                      leftSection={<IconRefresh size={16} />}
                    >
                      Refresh
                    </Button>
                  </Group>
                </Paper>

                <Paper p="md" withBorder>
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text fw={500} size="lg">
                        Macros ({filteredMacros.length})
                      </Text>
                      {macroTotalPages > 1 && (
                        <Group gap="md">
                          <Text size="sm" c="dimmed">
                            Showing {(macroPage - 1) * ITEMS_PER_PAGE + 1}-
                            {Math.min(
                              macroPage * ITEMS_PER_PAGE,
                              filteredMacros.length
                            )}{" "}
                            of {filteredMacros.length}
                          </Text>
                          <Pagination
                            total={macroTotalPages}
                            value={macroPage}
                            onChange={setMacroPage}
                            size="sm"
                          />
                        </Group>
                      )}
                    </Group>

                    {macrosLoading ? (
                      <Center p="xl">
                        <Loader />
                      </Center>
                    ) : filteredMacros.length === 0 ? (
                      <Text c="dimmed" ta="center" py="xl">
                        {macroSearch
                          ? "No macros match the current search"
                          : "No specimen macros found. Click 'Add Macro' to create one."}
                      </Text>
                    ) : (
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th style={{ width: 90 }}>Code</Table.Th>
                            <Table.Th>Description</Table.Th>
                            <Table.Th>Source</Table.Th>
                            <Table.Th>Subsite</Table.Th>
                            <Table.Th>Findings</Table.Th>
                            <Table.Th>Procedure</Table.Th>
                            <Table.Th>Material</Table.Th>
                            <Table.Th style={{ width: 100 }}>Status</Table.Th>
                            <Table.Th style={{ width: 90 }}>Actions</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {paginatedMacros.map((macro) => (
                            <Table.Tr
                              key={macro.id}
                              style={{
                                opacity: macro.isActive ? 1 : 0.5,
                              }}
                            >
                              <Table.Td>
                                <Text size="sm" fw={600} ff="monospace">
                                  {macro.code}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm" lineClamp={1}>
                                  {macro.description || "—"}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {macro.source?.name || "—"}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {macro.subsite?.name || "—"}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">{macro.findings || "—"}</Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {macro.collectionMethod || "—"}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {macro.material
                                    ? MATERIAL_LABELS[macro.material] ||
                                      macro.material
                                    : "—"}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Badge
                                  color={macro.isActive ? "green" : "red"}
                                  variant="light"
                                  size="sm"
                                >
                                  {macro.isActive ? "Active" : "Inactive"}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Group gap="xs" wrap="nowrap">
                                  <Tooltip label="Edit">
                                    <ActionIcon
                                      variant="subtle"
                                      color="blue"
                                      onClick={() => handleEditMacro(macro)}
                                    >
                                      <IconEdit size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                  <Tooltip
                                    label={
                                      macro.isActive ? "Deactivate" : "Activate"
                                    }
                                  >
                                    <ActionIcon
                                      variant="subtle"
                                      color={macro.isActive ? "red" : "green"}
                                      onClick={() => handleToggleMacro(macro)}
                                    >
                                      {macro.isActive ? (
                                        <IconX size={18} />
                                      ) : (
                                        <IconCheck size={18} />
                                      )}
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="batch" pt="md">
              <Stack gap="md">
                <Paper p="md" withBorder>
                  <Group>
                    <TextInput
                      placeholder="Search by name..."
                      value={batchSearch}
                      onChange={(e) => {
                        setBatchSearch(e.target.value);
                        setBatchPage(1);
                      }}
                      leftSection={<IconSearch size={16} />}
                      style={{ flex: 1 }}
                    />
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      onClick={() => refetchBatch()}
                      loading={batchLoading}
                      leftSection={<IconRefresh size={16} />}
                    >
                      Refresh
                    </Button>
                  </Group>
                </Paper>

                <Paper p="md" withBorder>
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text fw={500} size="lg">
                        Batch Templates ({filteredBatches.length})
                      </Text>
                      {batchTotalPages > 1 && (
                        <Group gap="md">
                          <Text size="sm" c="dimmed">
                            Showing {(batchPage - 1) * ITEMS_PER_PAGE + 1}-
                            {Math.min(
                              batchPage * ITEMS_PER_PAGE,
                              filteredBatches.length
                            )}{" "}
                            of {filteredBatches.length}
                          </Text>
                          <Pagination
                            total={batchTotalPages}
                            value={batchPage}
                            onChange={setBatchPage}
                            size="sm"
                          />
                        </Group>
                      )}
                    </Group>

                    {batchLoading ? (
                      <Center p="xl">
                        <Loader />
                      </Center>
                    ) : filteredBatches.length === 0 ? (
                      <Text c="dimmed" ta="center" py="xl">
                        {batchSearch
                          ? "No templates match the current search"
                          : "No batch templates found. Click 'Add Batch Template' to create one."}
                      </Text>
                    ) : (
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Name</Table.Th>
                            <Table.Th style={{ width: 140 }}>
                              Specimen Count
                            </Table.Th>
                            <Table.Th>Default Material</Table.Th>
                            <Table.Th style={{ width: 100 }}>Status</Table.Th>
                            <Table.Th style={{ width: 90 }}>Actions</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {paginatedBatches.map((template) => (
                            <Table.Tr
                              key={template.id}
                              style={{
                                opacity: template.isActive ? 1 : 0.5,
                              }}
                            >
                              <Table.Td>
                                <Text size="sm" fw={500}>
                                  {template.name}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">{template.specimenCount}</Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {template.defaultMaterial
                                    ? MATERIAL_LABELS[
                                        template.defaultMaterial
                                      ] || template.defaultMaterial
                                    : "—"}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Badge
                                  color={template.isActive ? "green" : "red"}
                                  variant="light"
                                  size="sm"
                                >
                                  {template.isActive ? "Active" : "Inactive"}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Group gap="xs" wrap="nowrap">
                                  <Tooltip label="Edit">
                                    <ActionIcon
                                      variant="subtle"
                                      color="blue"
                                      onClick={() => handleEditBatch(template)}
                                    >
                                      <IconEdit size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                  <Tooltip
                                    label={
                                      template.isActive
                                        ? "Deactivate"
                                        : "Activate"
                                    }
                                  >
                                    <ActionIcon
                                      variant="subtle"
                                      color={
                                        template.isActive ? "red" : "green"
                                      }
                                      onClick={() =>
                                        handleToggleBatch(template)
                                      }
                                    >
                                      {template.isActive ? (
                                        <IconX size={18} />
                                      ) : (
                                        <IconCheck size={18} />
                                      )}
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </Stack>

        {/* Macro Form Modal */}
        <Modal
          opened={macroFormOpened}
          onClose={closeMacroForm}
          title={editingMacro ? "Edit Specimen Macro" : "Add Specimen Macro"}
          size="lg"
          closeOnClickOutside={false}
          styles={{
            body: {
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              padding: 0,
            },
          }}
        >
          <ScrollArea.Autosize mah="60vh" px="md" pb="md">
            <SpecimenMacroForm
              formId="specimen-macro-form"
              initialValues={editingMacro || undefined}
              onSubmit={handleMacroSubmit}
            />
          </ScrollArea.Autosize>
          <Group
            justify="space-between"
            p="md"
            style={{
              borderTop: "1px solid var(--mantine-color-gray-3)",
              flexShrink: 0,
            }}
          >
            <Button variant="subtle" onClick={closeMacroForm}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="specimen-macro-form"
              loading={createMacro.isPending || updateMacro.isPending}
            >
              {editingMacro ? "Update" : "Create"}
            </Button>
          </Group>
        </Modal>

        {/* Batch Form Modal */}
        <Modal
          opened={batchFormOpened}
          onClose={closeBatchForm}
          title={editingBatch ? "Edit Batch Template" : "Add Batch Template"}
          size="md"
          closeOnClickOutside={false}
          styles={{
            body: {
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              padding: 0,
            },
          }}
        >
          <ScrollArea.Autosize mah="60vh" px="md" pb="md">
            <BatchTemplateForm
              formId="batch-template-form"
              initialValues={editingBatch || undefined}
              onSubmit={handleBatchSubmit}
            />
          </ScrollArea.Autosize>
          <Group
            justify="space-between"
            p="md"
            style={{
              borderTop: "1px solid var(--mantine-color-gray-3)",
              flexShrink: 0,
            }}
          >
            <Button variant="subtle" onClick={closeBatchForm}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="batch-template-form"
              loading={createBatch.isPending || updateBatch.isPending}
            >
              {editingBatch ? "Update" : "Create"}
            </Button>
          </Group>
        </Modal>

        {/* Macro Toggle Modal */}
        <Modal
          opened={macroConfirmOpened}
          onClose={closeMacroConfirm}
          title={
            macroToToggle?.isActive
              ? "Deactivate Specimen Macro"
              : "Activate Specimen Macro"
          }
          size="sm"
          centered
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {macroToToggle?.isActive ? "deactivate" : "activate"} the macro{" "}
              <strong>{macroToToggle?.code}</strong>?
            </Text>
            {macroToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated macros will not appear in the accessioning specimen
                entry.
              </Text>
            )}
            <Group justify="space-between" gap="xs">
              <Button variant="subtle" onClick={closeMacroConfirm}>
                Cancel
              </Button>
              <Button
                color={macroToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleMacro}
                loading={toggleMacroActive.isPending}
              >
                {macroToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Batch Toggle Modal */}
        <Modal
          opened={batchConfirmOpened}
          onClose={closeBatchConfirm}
          title={
            batchToToggle?.isActive
              ? "Deactivate Batch Template"
              : "Activate Batch Template"
          }
          size="sm"
          centered
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {batchToToggle?.isActive ? "deactivate" : "activate"} the template{" "}
              <strong>{batchToToggle?.name}</strong>?
            </Text>
            <Group justify="space-between" gap="xs">
              <Button variant="subtle" onClick={closeBatchConfirm}>
                Cancel
              </Button>
              <Button
                color={batchToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleBatch}
                loading={toggleBatchActive.isPending}
              >
                {batchToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
